import { href, useParams, type Register } from 'react-router';

/**
 * react-router types `href` as
 * `<Path extends keyof Args>(path: Path, ...args: Args[Path]) => string`, where
 * `Args` is the per-route param map. That map isn't exported, but it's derived
 * from the (exported, augmentable) `Register` interface — so we reconstruct it
 * here exactly as react-router does internally. This keeps the path↔params
 * correlation: passing the wrong params for a path is a type error, just like
 * with `href`. (Kept in sync with react-router's `href` typing.)
 */
type AnyParams = Record<string, string | undefined>;
type AnyPages = Record<string, { params: AnyParams }>;
type Pages = Register extends { pages: infer P extends AnyPages }
  ? P
  : AnyPages;
// `[Params] | []` when every param is optional (so the args object may be
// omitted), otherwise `[Params]`. Every user-facing route carries the optional
// `:locale?` segment, so its params object is always present in the map.
type ToArgs<Params extends AnyParams> =
  Partial<Params> extends Params ? [Params] | [] : [Params];
type Args = { [K in keyof Pages]: ToArgs<Pages[K]['params']> };
type HrefPath = keyof Args;

/** A locale-bound `href`: same call signature as `href`, locale pre-applied. */
export type BoundHref = <Path extends HrefPath>(
  path: Path,
  ...args: Args[Path]
) => string;

/**
 * Type-safe `href` that injects the `:locale?` segment. Same signature as
 * react-router's `href`, with `locale` as the first argument:
 *
 *   localeHref(locale, '/:locale?/experience/:experienceId', { experienceId })
 *
 * Use this **outside the React tree** — loaders, actions, `meta()`, and pure UI
 * components that receive `locale` via props. Inside route components prefer
 * {@link useLocaleHref}, which reads `params.locale` from the current URL so
 * links preserve the segment the visitor is on (rather than the server-resolved
 * storefront locale, which can differ).
 */
export function localeHref<Path extends HrefPath>(
  locale: string | undefined,
  path: Path,
  ...args: Args[Path]
): string {
  const params = (args as [AnyParams?])[0];
  // The explicit `locale` argument is authoritative — it wins over any `locale`
  // a caller happened to put in `params`.
  return (href as (p: string, pr?: object) => string)(path, {
    ...params,
    locale,
  });
}

/**
 * Hook variant of {@link localeHref}. Reads `locale` from the current route's
 * params and returns a locale-bound `href`. Use this **in React components** —
 * it preserves the locale segment the visitor is on rather than the
 * server-resolved storefront locale, which can differ.
 */
export function useLocaleHref(): BoundHref {
  const { locale } = useParams<{ locale?: string }>();
  return <Path extends HrefPath>(path: Path, ...args: Args[Path]): string =>
    localeHref(locale, path, ...args);
}

/**
 * Prepend `/{locale}` to a path that is NOT in the typesafe `href()` map.
 * Use for legacy routes still served by the Next.js app (`/request`,
 * `/upcoming-events`) and any other external string paths.
 *
 * Prefer {@link useLocaleHref} or {@link localeHref} for routes defined in
 * `routes.ts` — those are typesafe.
 */
export function localeHrefExternal(
  locale: string | undefined,
  path: string
): string {
  return locale ? `/${locale}${path}` : path;
}
